import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Breadcrumbs,
  Link,
  Alert,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import NotFound from '../NotFound';
import StaffAppBar from '../StaffAppBar';
import UserIdentityRail from './UserIdentityRail';
import OverviewTab from './OverviewTab';
import PermissionsTab from './PermissionsTab';
import { getUserDetail, ForbiddenError } from '../../services/userDetailService';

const VIEW_PERMISSION = 'can_view_user_details';

/**
 * Staff-facing detail page for one user.
 *
 * The permission check here is defence in depth, not the security control:
 * userPermissions lives in localStorage and the operator can edit it. The
 * control is the stacked middleware on GET /admin/users/:id. The two meet at
 * the same place — a ForbiddenError from the API renders the same 404 the local
 * check does — so an edited localStorage buys nothing but an extra round trip.
 */
function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const permissions = JSON.parse(localStorage.getItem('userPermissions') || '[]');
  const mayView = permissions.includes(VIEW_PERMISSION);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setForbidden(false);
      // getUserDetail either resolves with data or throws — there is no
      // longer a third, silent "returned undefined" case to guard against.
      const data = await getUserDetail(id);
      setDetail(data);
    } catch (err) {
      if (err instanceof ForbiddenError) {
        setForbidden(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!mayView) return;
    fetchDetail();
  }, [mayView, fetchDetail]);

  if (!mayView || forbidden) {
    return <NotFound />;
  }

  const displayName = detail
    ? [detail.first_name, detail.last_name].filter(Boolean).join(' ')
    : '';

  return (
    <Box sx={{ flexGrow: 1 }}>
      <StaffAppBar />

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
          >
            Admin Dashboard
          </Link>
          <Typography color="text.primary">{displayName || 'User'}</Typography>
        </Breadcrumbs>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && !detail && (
          <Card sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={220} />
          </Card>
        )}

        {detail && (
          <Card>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
              <UserIdentityRail
                detail={detail}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                isMobile={isMobile}
              />
              <Box
                role="tabpanel"
                id={`user-detail-tabpanel-${activeTab}`}
                aria-labelledby={`user-detail-tab-${activeTab}`}
                sx={{ flex: 1, p: 3 }}
              >
                {activeTab === 0 && <OverviewTab detail={detail} />}
                {activeTab === 1 && (
                  <PermissionsTab detail={detail} onRolesChanged={fetchDetail} />
                )}
              </Box>
            </Box>
          </Card>
        )}
      </Container>
    </Box>
  );
}

export default UserDetail;
